import classNames from 'classnames';
import React from 'react';
import isEqual from 'react-fast-compare';

import TableSingleInput from 'notebook/structured-tables/single-inputs/TableSingleInput';
import useSingleInputDragHandlers, {
  getStopDragPropagationProps,
} from 'notebook/structured-tables/single-inputs/useSingleInputDragHandlers';
import {TableEditableClass} from 'notebook/tables/core-ui/TableEditable';
import {TableHeaderRowProps} from 'notebook/tables/core-ui/TableHeaderRow';
import {TableRowProps} from 'notebook/tables/core-ui/TableRow';
import {ReorderOptions} from 'notebook/tables/excess/ops/TableOps';
import {RowModelProvider} from 'notebook/tables/store/contexts/RowModelContext';
import getTestProps from 'test/lib/cypress/getTestProps';

export interface TableSingleInputGroupProps {
  sliceStart: number;
  // exclusive
  sliceEnd: number;
  headerRowProps: TableHeaderRowProps;
  rowProps: TableRowProps;
  clearMultiSelect: TableEditableClass['clearMultiSelect'];
  columnWidths: Array<number>;
  setColumnWidth: (columnIndex: number, width: number) => void;
  locked?: boolean;
  tableIndex?: number;
  storeState?: () => void;
  reorderSingleInputs?: (tableIndex: number, options: ReorderOptions) => void;
}

export function TableSingleInputGroup(props: TableSingleInputGroupProps): JSX.Element {
  const {sliceStart, sliceEnd, headerRowProps, rowProps, tableIndex, storeState, reorderSingleInputs} = props;

  const numSingleInputs = sliceEnd - sliceStart;
  const canReorder = !headerRowProps.viewer && !headerRowProps.locked;

  const {dragSourceIndex, dragOverIndex, singleInputDragProps, dropZoneDragProps} =
    useSingleInputDragHandlers({
      numSingleInputs,
      canReorder,
      tableIndex,
      storeState,
      reorderSingleInputs,
    });

  const inputs = [];
  for (let index = 0; index < numSingleInputs; index++) {
    const j = sliceStart + index;
    const dropZoneIndex = numSingleInputs + index;
    const columnKey =
      headerRowProps.table.columnConstraints[j]?.structuredTableColumn?.provenanceUuid ?? `col-${j}`;

    inputs.push(
      <div
        key={columnKey}
        className="mediocre-tableEditable-singleInput-wrapper"
        {...getTestProps({component: 'SingleInputGroup', element: `wrapper-${j}-${columnKey}`})}
      >
        <TableSingleInput
          j={j}
          headerRowProps={headerRowProps}
          rowProps={rowProps}
          columnWidth={props.columnWidths[j]}
          setColumnWidth={props.setColumnWidth}
          locked={props.locked}
          dragIndex={index}
          isDragging={dragSourceIndex === index}
          isDragOver={dragOverIndex === index && dragSourceIndex !== index}
          {...singleInputDragProps}
        />
        {singleInputDragProps.canDrag && dragSourceIndex != null && (
          <div
            className={classNames('mediocre-tableEditable-singleInput-dropZone', {
              'mediocre-tableEditable-singleInput-dropZone--active': dragOverIndex === dropZoneIndex,
            })}
            data-drop-zone-index={dropZoneIndex}
            {...dropZoneDragProps}
            {...getTestProps({component: 'SingleInputGroup', element: `dropZone-${index}`})}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={classNames('mediocre-tableEditable-singleInputGroup', {
        'mediocre-tableEditable-singleInputGroup--dragging': dragSourceIndex != null,
      })}
      onClick={props.clearMultiSelect}
      {...getStopDragPropagationProps()}
      {...getTestProps({component: 'SingleInputGroup', element: 'root'})}
    >
      <RowModelProvider rowIndex={rowProps.i}>{inputs}</RowModelProvider>
    </div>
  );
}

// This needs to be memoized to prevent it from re-rendering when other rows / cells change
// TODO(BNCH-113857): This can probably be removed once selectors are enabled
export default React.memo(
  TableSingleInputGroup,
  (prevProps, nextProps) =>
    isEqual(prevProps.rowProps, nextProps.rowProps) &&
    isEqual(prevProps.columnWidths, nextProps.columnWidths) &&
    prevProps.locked === nextProps.locked &&
    prevProps.tableIndex === nextProps.tableIndex &&
    prevProps.storeState === nextProps.storeState &&
    prevProps.reorderSingleInputs === nextProps.reorderSingleInputs
);
